import { useCallback, useMemo, useState } from 'react'

import { FilterIcon } from 'lucide-react'
import { Button, ComboboxMulti, DefaultPopover, Label, Separator } from 'twyne-new.ui'

import { getChartLegendLabel } from '../utils/chartFormatters'

export const EMPTY_CHART_FILTER = { keys: [], indexes: [] }

export function isChartFilterActive(filter) {
  return Boolean(filter?.keys?.length || filter?.indexes?.length)
}

function toVisibleValues(options, hidden = []) {
  return options.map(option => option.value).filter(value => !hidden.includes(value))
}

function toHiddenValues(options, visible = []) {
  const selected = new Set(visible.includes('all') ? options.map(option => option.value) : visible)

  return options.map(option => option.value).filter(value => !selected.has(value))
}

export default function ChartFilterPopover({ result, collection, filter, onChange }) {
  const [open, setOpen] = useState(false)

  const seriesOptions = useMemo(
    () => (result?.keys || []).map(key => ({
      value: String(key),
      label: getChartLegendLabel(collection, key, result?.keyType) || String(key),
    })),
    [collection, result?.keys, result?.keyType],
  )

  const indexOptions = useMemo(
    () => (result?.data || []).map(row => ({
      value: String(row.date),
      label: String(row.date),
    })),
    [result?.data],
  )

  const visibleSeries = useMemo(
    () => toVisibleValues(seriesOptions, filter?.keys),
    [seriesOptions, filter?.keys],
  )

  const visibleIndexes = useMemo(
    () => toVisibleValues(indexOptions, filter?.indexes),
    [indexOptions, filter?.indexes],
  )

  const handleSeriesChange = useCallback(visible => {
    onChange({ ...filter, keys: toHiddenValues(seriesOptions, visible) })
  }, [filter, onChange, seriesOptions])

  const handleIndexesChange = useCallback(visible => {
    onChange({ ...filter, indexes: toHiddenValues(indexOptions, visible) })
  }, [filter, onChange, indexOptions])

  const handleReset = useCallback(() => {
    onChange(EMPTY_CHART_FILTER)
  }, [onChange])

  const active = isChartFilterActive(filter)
  const hasIndexes = indexOptions.length > 1

  return (
    <DefaultPopover
      title="Chart filter"
      wrapperProps={{ open, onOpenChange: setOpen }}
      triggerProps={{ asChild: true }}
      contentProps={{ className: 'w-80 p-3', align: 'end' }}
      trigger={
        <Button
          type="button"
          size="sm"
          className="px-1"
          variant={active ? 'light' : 'ghost'}
          color={active ? 'accent' : 'neutral'}
          aria-label="Filter this chart"
        >
          <FilterIcon size={16} />
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1.5">
          <Label>Series ({visibleSeries.length}/{seriesOptions.length})</Label>
          <ComboboxMulti
            options={seriesOptions}
            value={visibleSeries}
            onChange={handleSeriesChange}
            placeholder="No series"
            maxCount={1}
            disabled={!seriesOptions.length}
          />
        </div>

        {hasIndexes && <div className="flex flex-col gap-1.5">
          <Label>X axis ({visibleIndexes.length}/{indexOptions.length})</Label>
          <ComboboxMulti
            options={indexOptions}
            value={visibleIndexes}
            onChange={handleIndexesChange}
            placeholder="No values"
            maxCount={1}
          />
        </div>}

        {active && <>
          <Separator className="my-0" />
          <div className="flex justify-end">
            <Button size="sm" variant="ghost" color="danger" onClick={handleReset}>
              Reset filter
            </Button>
          </div>
        </>}
      </div>
    </DefaultPopover>
  )
}
